import {
    useState, useEffect
} from 'react';
import {
    useSearchParams, useNavigate
} from 'react-router-dom';
import coolriots from 'assets/coolriots.png';

const BASE_URL = process.env.REACT_APP_BEX_SERVER_URL || '';

export default function ProvisionedConfigAuth() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const instanceId = searchParams.get('instance') || '';

    const [keyInput, setKeyInput] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!keyInput.trim()) return;

        setLoading(true);
        setError('');

        try {
            const res = await fetch(`${BASE_URL}/api/assistant/provisioning/authenticate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    instance_id: instanceId,
                    auth_key: keyInput.trim()
                }),
            });

            const json = await res.json();

            if (!res.ok) {
                setError(json.message || 'Invalid configuration key.');
                setLoading(false);
                return;
            }

            // Store raw auth key in cookie.
            // SameSite=None + Secure + Partitioned so the cookie survives when this
            // page is rendered inside the IBM Cloud catalog iframe (cross-site
            // top-level). SameSite=Strict here was being dropped by browsers and
            // bounced the user back to the auth form after every successful login.
            document.cookie = `provisioned-config-key=${json.data.auth_key}; path=/; SameSite=None; Secure; Partitioned; max-age=${7 * 24 * 60 * 60}`;

            // Redirect to config page
            navigate(`/provisioned-config?instance=${instanceId}`, {
                replace: true
            });
        } catch {
            setError('Unable to connect to the server.');
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen items-center justify-center bg-background">
            <div className="w-full max-w-sm p-6 border rounded-lg bg-card shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                    <img src={coolriots} alt="coolriots" className="h-6" />
                </div>
                <h2 className="text-lg font-semibold mb-1">Configuration Access</h2>
                <p className="text-sm text-muted-foreground mb-4">
                    Enter your configuration key to access this assistant.
                </p>
                {!instanceId && (
                    <p className="text-sm text-destructive mb-4">Missing instance ID in URL.</p>
                )}
                <form onSubmit={handleSubmit}>
                    <input
                        type="password"
                        placeholder="Enter configuration key"
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md text-sm mb-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        disabled={loading || !instanceId}
                        autoFocus
                    />
                    {error && (
                        <p className="text-sm text-destructive mb-2">{error}</p>
                    )}
                    <button
                        type="submit"
                        disabled={loading || !instanceId || !keyInput.trim()}
                        className="w-full px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
                    >
                        {loading ? 'Verifying...' : 'Access Configuration'}
                    </button>
                </form>
            </div>
        </div>
    );
}
